import { CheckIcon } from "@radix-ui/react-icons";
import { EmailAddressesService, PersonalAddress } from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import CopyLinkRow from "components/PublicBooking/CopyLinkRow";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { forgetPersonalAddresses } from "util/personalAddress";

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-[60vh] bg-gray-50 px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-[560px] rounded-2xl bg-white p-6 shadow-sm sm:p-8">
        {children}
      </div>
    </div>
  );
}

/**
 * The page the link in a personal address's confirmation email opens. It
 * confirms the address at once for the admin who added it, signed in (the
 * route asks them to sign in first), and says where to forward its mail.
 */
export default function ConfirmEmailPage() {
  const { token } = useParams();
  const [address, setAddress] = useState<PersonalAddress>();
  const [failed, setFailed] = useState<string>();
  // Once: opening the link again (or React running effects twice) would
  // find it used.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    EmailAddressesService.confirmPersonalAddress({ requestBody: { token } })
      .then((confirmed) => {
        forgetPersonalAddresses(); // reply boxes fetch it again, confirmed
        setAddress(confirmed);
      })
      .catch((e) =>
        setFailed(
          errorText(
            e,
            "Couldn't confirm the address. Check your connection and try again."
          )
        )
      );
  }, [token]);

  if (failed) {
    return (
      <Card>
        <h1 className="text-xl font-semibold text-gray-900">
          This link didn&apos;t work.
        </h1>
        <p className="mt-2 text-sm text-gray-700">{failed}</p>
        <Link
          to="/dashboard"
          className="mt-5 inline-block text-sm font-semibold text-[color:var(--green)] hover:underline"
        >
          Go to Tadpole
        </Link>
      </Card>
    );
  }

  if (!address) {
    return (
      <Card>
        <p className="text-sm text-gray-700">Confirming your address…</p>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
        <CheckIcon className="h-6 w-6 text-[color:var(--green)]" aria-hidden />
        {address.email} is confirmed.
      </h1>
      <p className="mt-2 text-sm text-gray-700">
        {address.sends_from === address.email
          ? `Your one-to-one emails and replies now go from ${address.email}.`
          : address.reply_to === address.email
          ? `Your one-to-one emails and replies go from ${address.sends_from}, and replies to them come to ${address.email}.`
          : "Tadpole isn't using it yet, because its incoming email isn't fully set up. Contact Tadpole support."}
      </p>
      {address.forward_to && (
        <>
          <p className="mt-4 text-sm text-gray-700">
            To bring its mail into Tadpole, have this mailbox forward its mail
            to this address. What comes in that way shows only to you.
          </p>
          <CopyLinkRow
            url={address.forward_to}
            open={false}
            copied="Address copied"
          />
        </>
      )}
      <Link
        to="/dashboard"
        className="mt-5 inline-block text-sm font-semibold text-[color:var(--green)] hover:underline"
      >
        Go to Tadpole
      </Link>
    </Card>
  );
}
